import type { MtbTrailCategory, MtbTrailCharacter, MtbTrailRelatedPlace } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Tag } from "~/components/ui/tag";
import { CHARACTER_LABELS } from "./mtbTrailLabels";

const CATEGORY_SHORT: Record<MtbTrailCategory, { label: string; title?: string }> = {
  xc: { label: "XC", title: "Cross-country" },
  enduro: { label: "Enduro" },
  downhill: { label: "DH", title: "Downhill" },
  freeride: { label: "Freeride" },
  "dirt-jump": { label: "DJ", title: "Dirt Jump" },
};

const MAX_CHARACTER_TAGS = 2;

interface RidingSignatureProps {
  trails: MtbTrailRelatedPlace[];
  className?: string;
}

export function RidingSignature({ trails, className }: RidingSignatureProps) {
  const categories = new Set<MtbTrailCategory>();
  const characterCounts = new Map<MtbTrailCharacter, number>();

  for (const trail of trails) {
    const metadata = trail.metadata;
    if (!metadata) continue;
    if (metadata.category) categories.add(metadata.category);
    for (const tag of metadata.character ?? []) {
      characterCounts.set(tag, (characterCounts.get(tag) ?? 0) + 1);
    }
  }

  const topCharacter = [...characterCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_CHARACTER_TAGS)
    .map(([tag]) => tag);

  if (categories.size === 0 && topCharacter.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {[...categories].map((cat) => {
        const { label, title } = CATEGORY_SHORT[cat];
        return (
          <Tag key={cat} title={title} aria-label={title} className="text-muted-foreground">
            {label}
          </Tag>
        );
      })}
      {topCharacter.map((tag) => (
        <Tag key={tag} className="text-muted-foreground">
          {CHARACTER_LABELS[tag]}
        </Tag>
      ))}
    </div>
  );
}
